import axios from "axios";
import { create } from "zustand";
import { LOG } from "../utils/logUtil";

interface ICumulativeUsersDataPoint {
  period: string;
  date: string;
  cumulative_downloads: number;
  cumulative_droppers: number;
  cumulative_5plus: number;
  week_start?: string;
  week_end?: string;
  year?: number;
  quarter?: string;
}

interface ICumulativeUsersResponse {
  success: boolean;
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: ICumulativeUsersDataPoint[];
}

interface IConversionsDataPoint {
  period: string;
  date: string;
  percentage_active_plastic_over_total: number;
  percentage_plastic_not_active_over_total: number;
  percentage_material_no_plastic_over_total: number;
  percentage_statie_only_over_total: number;
  percentage_statie_pro_over_total?: number;
  statie_only_no_pro?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface IConversionsResponse {
  success: boolean;
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IConversionsDataPoint[];
}

interface ICumulativeNestedDataPoint {
  period: string;
  date: string;
  cumulative_droppers: number;
  cumulative_droppers_plastic: number;
  cumulative_active_plastic: number;
  percentage_plastic_over_total: number;
  week_start?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface ICumulativeNestedResponse {
  success: boolean;
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: ICumulativeNestedDataPoint[];
}

interface IVolumesDataPoint {
  period: string;
  date: string;
  store_name?: string; // For location-based
  city?: string; // For city-based
  plastic_weight_per_week?: number;
  plastic_droppers_per_week?: number;
  new_plastic_droppers?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface IVolumesResponse {
  success: boolean;
  type: "city" | "location";
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IVolumesDataPoint[];
}

interface IAvgDropSizeDataPoint {
  period: string;
  date: string;
  store_name?: string; // For location-based
  city?: string; // For city-based
  avg_plastic_drop_size: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface IAvgDropSizeResponse {
  success: boolean;
  type: "city" | "location";
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IAvgDropSizeDataPoint[];
}

interface IMonthsSinceOpeningDataPoint {
  period: string;
  date: string;
  store_name?: string;
  months_since_opening?: number;
  plastic_weight_per_week?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface IMonthsSinceOpeningResponse {
  success: boolean;
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IMonthsSinceOpeningDataPoint[];
}

interface IPlasticActivationDataPoint {
  period: string;
  date: string;
  store_name?: string;
  plastic_activation?: number;
  plastic_activation_percentage?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface IPlasticActivationResponse {
  success: boolean;
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IPlasticActivationDataPoint[];
}

interface IKeyMetricsDataPoint {
  store_name?: string; // For location-based
  city?: string; // For city-based
  plastic_weight_per_week: number;
  total_statie_items_per_week: number;
  plastic_droppers_per_week: number;
  cumulative_droppers: number;
  cumulative_plastic_droppers: number;
  cumulative_new_active_plastic_droppers: number;
  months_since_opening?: number; // Months since opening from backend
  period: string;
  date?: string;
  week_start?: string;
  month_start?: string;
  year?: number;
}

interface IKeyMetricsResponse {
  success: boolean;
  type: "city" | "location";
  period: string;
  from: string | null;
  to: string | null;
  count: number;
  data: IKeyMetricsDataPoint[];
}

interface IStore {
  cumulativeUsersData: ICumulativeUsersDataPoint[] | null;
  isLoadingCumulativeUsers: boolean;
  cumulativeDroppersData: ICumulativeUsersDataPoint[] | null;
  isLoadingCumulativeDroppers: boolean;
  conversionsData: IConversionsDataPoint[] | null;
  isLoadingConversions: boolean;
  cumulativeNestedData: ICumulativeNestedDataPoint[] | null;
  isLoadingCumulativeNested: boolean;

  // Volumes data
  volumesPerWeightData: IVolumesDataPoint[] | null;
  isLoadingVolumesPerWeight: boolean;
  volumesPerDroppersData: IVolumesDataPoint[] | null;
  isLoadingVolumesPerDroppers: boolean;
  volumesNewDroppersData: IVolumesDataPoint[] | null;
  isLoadingVolumesNewDroppers: boolean;
  avgDropSizeData: IAvgDropSizeDataPoint[] | null;
  isLoadingAvgDropSize: boolean;
  monthsSinceOpeningData: IMonthsSinceOpeningDataPoint[] | null;
  isLoadingMonthsSinceOpening: boolean;
  monthsSinceOpeningFrom: string | null;
  monthsSinceOpeningTo: string | null;
  plasticActivationData: IPlasticActivationDataPoint[] | null;
  isLoadingPlasticActivation: boolean;
  plasticActivationFrom: string | null;
  plasticActivationTo: string | null;
  keyMetricsData: IKeyMetricsDataPoint[] | null;
  isLoadingKeyMetrics: boolean;

  getCumulativeUsers: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    fromQuarter?: string;
    toQuarter?: string;
  }) => Promise<void>;

  getCumulativeDroppers: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    fromQuarter?: string;
    toQuarter?: string;
  }) => Promise<void>;

  getConversions: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
  }) => Promise<void>;

  getCumulativeNested: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
  }) => Promise<void>;

  getVolumesPerWeight: (params: {
    type: "city" | "location";
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
    cities?: string[];
  }) => Promise<void>;

  getVolumesPerDroppers: (params: {
    type: "city" | "location";
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
    cities?: string[];
  }) => Promise<void>;

  getVolumesNewDroppers: (params: {
    type: "city" | "location";
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
    cities?: string[];
  }) => Promise<void>;

  getAvgDropSize: (params: {
    type: "city" | "location";
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
    cities?: string[];
  }) => Promise<void>;

  getMonthsSinceOpening: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
  }) => Promise<void>;

  getPlasticActivation: (params: {
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
  }) => Promise<void>;

  getKeyMetrics: (params: {
    type: "city" | "location";
    period: "weekly" | "monthly" | "quarterly" | "yearly";
    fromYear?: number;
    fromWeek?: number;
    toYear?: number;
    toWeek?: number;
    year?: number;
    fromMonth?: number;
    toMonth?: number;
    locations?: string[];
    cities?: string[];
  }) => Promise<void>;
}

export const usePlasticDashboard = create<IStore>()((set) => ({
  cumulativeUsersData: null,
  isLoadingCumulativeUsers: false,
  cumulativeDroppersData: null,
  isLoadingCumulativeDroppers: false,
  conversionsData: null,
  isLoadingConversions: false,
  cumulativeNestedData: null,
  isLoadingCumulativeNested: false,

  // Volumes data
  volumesPerWeightData: null,
  isLoadingVolumesPerWeight: false,
  volumesPerDroppersData: null,
  isLoadingVolumesPerDroppers: false,
  volumesNewDroppersData: null,
  isLoadingVolumesNewDroppers: false,
  avgDropSizeData: null,
  isLoadingAvgDropSize: false,
  monthsSinceOpeningData: null,
  isLoadingMonthsSinceOpening: false,
  monthsSinceOpeningFrom: null,
  monthsSinceOpeningTo: null,
  plasticActivationData: null,
  isLoadingPlasticActivation: false,
  plasticActivationFrom: null,
  plasticActivationTo: null,
  keyMetricsData: null,
  isLoadingKeyMetrics: false,

  getCumulativeUsers: async (params) => {
    set({ isLoadingCumulativeUsers: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      const response = await axios.get<ICumulativeUsersResponse>(
        "/plastic-dashboard/cumulative-users",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ cumulativeUsersData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching cumulative users", { error });
      set({ cumulativeUsersData: null });
    } finally {
      set({ isLoadingCumulativeUsers: false });
    }
  },

  getCumulativeDroppers: async (params) => {
    set({ isLoadingCumulativeDroppers: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      const response = await axios.get<ICumulativeUsersResponse>(
        "/plastic-dashboard/cumulative-droppers",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ cumulativeDroppersData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching cumulative droppers", { error });
      set({ cumulativeDroppersData: null });
    } finally {
      set({ isLoadingCumulativeDroppers: false });
    }
  },

  getConversions: async (params) => {
    set({ isLoadingConversions: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      const response = await axios.get<IConversionsResponse>(
        "/plastic-dashboard/conversions",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ conversionsData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching conversions", { error });
      set({ conversionsData: null });
    } finally {
      set({ isLoadingConversions: false });
    }
  },

  getCumulativeNested: async (params) => {
    set({ isLoadingCumulativeNested: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      const response = await axios.get<ICumulativeNestedResponse>(
        "/plastic-dashboard/cumulative-nested",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ cumulativeNestedData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching cumulative nested", { error });
      set({ cumulativeNestedData: null });
    } finally {
      set({ isLoadingCumulativeNested: false });
    }
  },

  getVolumesPerWeight: async (params) => {
    set({ isLoadingVolumesPerWeight: true });
    try {
      const queryParams: Record<string, string> = {
        type: params.type,
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location/city filters
      if (
        params.type === "location" &&
        params.locations &&
        params.locations.length > 0
      ) {
        queryParams.location = params.locations.join(",");
      } else if (
        params.type === "city" &&
        params.cities &&
        params.cities.length > 0
      ) {
        queryParams.city = params.cities.join(",");
      }

      const response = await axios.get<IVolumesResponse>(
        "/plastic-volumes/per-weight",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ volumesPerWeightData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching volumes per weight", { error });
      set({ volumesPerWeightData: null });
    } finally {
      set({ isLoadingVolumesPerWeight: false });
    }
  },

  getVolumesPerDroppers: async (params) => {
    set({ isLoadingVolumesPerDroppers: true });
    try {
      const queryParams: Record<string, string> = {
        type: params.type,
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location/city filters
      if (
        params.type === "location" &&
        params.locations &&
        params.locations.length > 0
      ) {
        queryParams.location = params.locations.join(",");
      } else if (
        params.type === "city" &&
        params.cities &&
        params.cities.length > 0
      ) {
        queryParams.city = params.cities.join(",");
      }

      const response = await axios.get<IVolumesResponse>(
        "/plastic-volumes/per-plastic-droppers",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ volumesPerDroppersData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching volumes per droppers", { error });
      set({ volumesPerDroppersData: null });
    } finally {
      set({ isLoadingVolumesPerDroppers: false });
    }
  },

  getVolumesNewDroppers: async (params) => {
    set({ isLoadingVolumesNewDroppers: true });
    try {
      const queryParams: Record<string, string> = {
        type: params.type,
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location/city filters
      if (
        params.type === "location" &&
        params.locations &&
        params.locations.length > 0
      ) {
        queryParams.location = params.locations.join(",");
      } else if (
        params.type === "city" &&
        params.cities &&
        params.cities.length > 0
      ) {
        queryParams.city = params.cities.join(",");
      }

      const response = await axios.get<IVolumesResponse>(
        "/plastic-volumes/new-plastic-droppers",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ volumesNewDroppersData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching volumes new droppers", { error });
      set({ volumesNewDroppersData: null });
    } finally {
      set({ isLoadingVolumesNewDroppers: false });
    }
  },

  getAvgDropSize: async (params) => {
    set({ isLoadingAvgDropSize: true });
    try {
      const queryParams: Record<string, string> = {
        type: params.type,
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location/city filters
      if (
        params.type === "location" &&
        params.locations &&
        params.locations.length > 0
      ) {
        queryParams.location = params.locations.join(",");
      } else if (
        params.type === "city" &&
        params.cities &&
        params.cities.length > 0
      ) {
        queryParams.city = params.cities.join(",");
      }

      const response = await axios.get<IAvgDropSizeResponse>(
        "/plastic-volumes/avg-drop-size",
        { params: queryParams }
      );

      if (response.data.success) {
        set({ avgDropSizeData: response.data.data });
      }
    } catch (error) {
      LOG.error("Error fetching avg drop size", { error });
      set({ avgDropSizeData: null });
    } finally {
      set({ isLoadingAvgDropSize: false });
    }
  },

  getMonthsSinceOpening: async (params) => {
    set({ isLoadingMonthsSinceOpening: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.year) queryParams.year = String(params.year);
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location filter (only location-based, not city)
      if (params.locations && params.locations.length > 0) {
        queryParams.location = params.locations.join(",");
      }

      const response = await axios.get<IMonthsSinceOpeningResponse>(
        "/plastic-volumes/months-since-opening",
        { params: queryParams }
      );

      if (response.data.success) {
        set({
          monthsSinceOpeningData: response.data.data,
          monthsSinceOpeningFrom: response.data.from,
          monthsSinceOpeningTo: response.data.to,
        });
      }
    } catch (error) {
      LOG.error("Error fetching months since opening", { error });
      set({ monthsSinceOpeningData: null });
    } finally {
      set({ isLoadingMonthsSinceOpening: false });
    }
  },

  getPlasticActivation: async (params) => {
    set({ isLoadingPlasticActivation: true });
    try {
      const queryParams: Record<string, string> = {
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      // Add location filter (only location-based, not city)
      if (params.locations && params.locations.length > 0) {
        queryParams.location = params.locations.join(",");
      }

      const response = await axios.get<IPlasticActivationResponse>(
        "/plastic-volumes/plastic-activation",
        { params: queryParams }
      );

      if (response.data.success) {
        set({
          plasticActivationData: response.data.data,
          plasticActivationFrom: response.data.from,
          plasticActivationTo: response.data.to,
        });
      }
    } catch (error) {
      LOG.error("Error fetching plastic activation", { error });
      set({ plasticActivationData: null });
    } finally {
      set({ isLoadingPlasticActivation: false });
    }
  },

  getKeyMetrics: async (params) => {
    set({ isLoadingKeyMetrics: true });
    try {
      const queryParams: Record<string, string> = {
        type: params.type,
        period: params.period,
      };

      if (params.period === "weekly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromWeek) queryParams.fromWeek = String(params.fromWeek);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toWeek) queryParams.toWeek = String(params.toWeek);
      } else if (params.period === "monthly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.fromMonth) queryParams.fromMonth = String(params.fromMonth);
        if (params.toYear) queryParams.toYear = String(params.toYear);
        if (params.toMonth) queryParams.toMonth = String(params.toMonth);
      } else if (params.period === "quarterly") {
        if (params.year) queryParams.year = String(params.year);
      } else if (params.period === "yearly") {
        if (params.fromYear) queryParams.fromYear = String(params.fromYear);
        if (params.toYear) queryParams.toYear = String(params.toYear);
      }

      if (
        params.type === "location" &&
        params.locations &&
        params.locations.length > 0
      ) {
        queryParams.location = params.locations.join(",");
      } else if (
        params.type === "city" &&
        params.cities &&
        params.cities.length > 0
      ) {
        queryParams.city = params.cities.join(",");
      }

      const response = await axios.get<IKeyMetricsResponse>(
        "/plastic-volumes/key-metrics",
        { params: queryParams }
      );

      if (response.data.success) {
        set({
          keyMetricsData: response.data.data,
        });
      }
    } catch (error) {
      LOG.error("Error fetching key metrics", { error });
      set({ keyMetricsData: null });
    } finally {
      set({ isLoadingKeyMetrics: false });
    }
  },
}));
